import React, { useEffect, useState } from "react";

const ACTIONABLE_SELECTOR = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[role='button']:not([aria-disabled='true'])",
  "[role='tab']:not([aria-disabled='true'])",
  "[contenteditable='true']",
  "[data-academy-action]",
].join(",");

const INDICATOR_SIZE = 38;
const INDICATOR_GAP = 9;
const VIEWPORT_GAP = 10;

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), maximum);
}

function sameLayout(previous, next) {
  return (
    previous &&
    previous.placement === next.placement &&
    Math.abs(previous.left - next.left) < 1 &&
    Math.abs(previous.top - next.top) < 1
  );
}

function getIndicatorLayout(target, avoidPlacement) {
  if (!target?.isConnected || target.getClientRects().length === 0) {
    return null;
  }

  const rect = target.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  if (
    rect.right < 0 ||
    rect.bottom < 0 ||
    rect.left > viewportWidth ||
    rect.top > viewportHeight
  ) {
    return null;
  }

  const sideTop = clamp(
    rect.top + Math.min(Math.max(rect.height * 0.28, 8), 32),
    VIEWPORT_GAP,
    viewportHeight - INDICATOR_SIZE - VIEWPORT_GAP,
  );

  const horizontal = clamp(
    rect.left + Math.max(0, rect.width - INDICATOR_SIZE),
    VIEWPORT_GAP,
    viewportWidth - INDICATOR_SIZE - VIEWPORT_GAP,
  );
  const candidates = [
    {
      placement: "right",
      left: rect.right + INDICATOR_GAP,
      top: sideTop,
      fits:
        rect.right + INDICATOR_GAP + INDICATOR_SIZE <= viewportWidth,
    },
    {
      placement: "left",
      left: rect.left - INDICATOR_GAP - INDICATOR_SIZE,
      top: sideTop,
      fits: rect.left - INDICATOR_GAP - INDICATOR_SIZE >= 0,
    },
    {
      placement: "bottom",
      left: horizontal,
      top: rect.bottom + INDICATOR_GAP,
      fits:
        rect.bottom + INDICATOR_GAP + INDICATOR_SIZE <= viewportHeight,
    },
    {
      placement: "top",
      left: horizontal,
      top: rect.top - INDICATOR_GAP - INDICATOR_SIZE,
      fits: rect.top - INDICATOR_GAP - INDICATOR_SIZE >= 0,
    },
  ].filter((candidate) => candidate.fits);

  const selected =
    candidates.find((candidate) => candidate.placement !== avoidPlacement) ||
    candidates[0];

  if (selected) {
    const { fits, ...layout } = selected;
    return layout;
  }

  return {
    placement: "top",
    left: horizontal,
    top: clamp(
      rect.top - INDICATOR_SIZE / 2,
      VIEWPORT_GAP,
      viewportHeight - INDICATOR_SIZE - VIEWPORT_GAP,
    ),
  };
}

export function resolveTutorialActionTarget(element, force = false) {
  if (!element) return null;
  if (force || element.matches?.(ACTIONABLE_SELECTOR)) return element;

  const descendants = Array.from(
    element.querySelectorAll?.(ACTIONABLE_SELECTOR) || [],
  ).filter((candidate) => candidate.getClientRects().length > 0);

  return descendants.length === 1 ? descendants[0] : null;
}

export default function TutorialTargetIndicator({
  active = true,
  avoidPlacement,
  target,
}) {
  const [layout, setLayout] = useState(null);

  useEffect(() => {
    if (!active || !target) {
      setLayout(null);
      return undefined;
    }

    const update = () => {
      const next = getIndicatorLayout(target, avoidPlacement);
      setLayout((previous) => {
        if (!next) return previous === null ? previous : null;
        return sameLayout(previous, next) ? previous : next;
      });
    };

    update();

    const resizeObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(update)
        : null;
    resizeObserver?.observe(target);

    const interval = window.setInterval(update, 400);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);

    return () => {
      resizeObserver?.disconnect();
      window.clearInterval(interval);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [active, avoidPlacement, target]);

  if (!active || !layout) return null;

  return (
    <div
      className={`academyTargetIndicator academyTargetIndicator--${layout.placement}`}
      style={{ left: `${layout.left}px`, top: `${layout.top}px` }}
      data-academy-target-indicator="true"
      aria-hidden="true"
    >
      <span className="academyTargetIndicatorHalo" />
      <span className="academyTargetIndicatorIcon">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M6 3.5v16l4.1-3.6 3.2 5.5 3.2-1.8-3.1-5.4 5.3-.9L6 3.5Z" />
          <path d="M15.5 2v3.2M21 7.5h-3.2M19.4 3.6l-2.3 2.3" />
        </svg>
      </span>
    </div>
  );
}
